import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Keyboard } from 'lucide-react';

interface ShortcutGroup {
  title: string;
  shortcuts: Array<{
    keys: string[];
    description: string;
  }>;
}

const BUYER_SHORTCUTS: ShortcutGroup[] = [
  {
    title: 'Navigation',
    shortcuts: [
      { keys: ['G', 'D'], description: 'Go to Dashboard' },
      { keys: ['G', 'I'], description: 'Go to Inventory' },
      { keys: ['G', 'S'], description: 'Go to Suppliers' },
      { keys: ['G', 'Q'], description: 'Go to Quotes' },
      { keys: ['G', 'O'], description: 'Go to Orders' },
    ],
  },
  {
    title: 'Actions',
    shortcuts: [
      { keys: ['C'], description: 'Create RFQ' },
      { keys: ['N'], description: 'Add Inventory Item' },
      { keys: ['/'], description: 'Focus Search' },
      { keys: ['?'], description: 'Show Shortcuts' },
    ],
  },
  {
    title: 'General',
    shortcuts: [
      { keys: ['⌘', 'K'], description: 'Open Command Palette' },
      { keys: ['Ctrl', 'K'], description: 'Open Command Palette (Windows)' },
      { keys: ['Esc'], description: 'Close Modal / Clear Selection' },
    ],
  },
];

export function KeyboardShortcutsHelp() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === '?' && !event.ctrlKey && !event.metaKey) {
        const target = event.target as HTMLElement;
        if (
          target.tagName !== 'INPUT' &&
          target.tagName !== 'TEXTAREA' &&
          target.contentEditable !== 'true'
        ) {
          event.preventDefault();
          setOpen(true);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setOpen(true)}
            aria-label="Keyboard shortcuts"
            className="size-9 min-h-[44px] min-w-[44px]"
          >
            <Keyboard className="size-4" aria-hidden />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Keyboard shortcuts (press ?)</p>
        </TooltipContent>
      </Tooltip>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Keyboard className="size-5" aria-hidden />
              Keyboard Shortcuts
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4 space-y-6">
            {BUYER_SHORTCUTS.map((group) => (
              <div key={group.title}>
                <h3 className="mb-3 font-medium">{group.title}</h3>
                <div className="space-y-2">
                  {group.shortcuts.map((shortcut, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between rounded-md px-3 py-2 hover:bg-muted/50"
                    >
                      <span className="text-sm">{shortcut.description}</span>
                      <div className="flex flex-wrap items-center justify-end gap-1">
                        {shortcut.keys.map((key, keyIndex) => (
                          <React.Fragment key={keyIndex}>
                            {keyIndex > 0 && (
                              <span className="mx-1 text-xs text-muted-foreground">
                                then
                              </span>
                            )}
                            <Badge variant="outline" className="font-mono text-xs">
                              {key}
                            </Badge>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Press{' '}
            <Badge variant="outline" className="mx-1 font-mono">
              ?
            </Badge>{' '}
            to show this dialog anytime
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}
